import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, SlidersHorizontal, ArrowUp, ArrowDown, X } from 'lucide-react';

import { analyticsApi } from '../../../api/analytics.js';
import { storesApi } from '../../../api/stores.js';
import { usersApi } from '../../../api/users.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { KpiCard } from '../../../components/ui/KpiCard.jsx';
import { Tabs } from '../../../components/ui/Tabs.jsx';
import { RangeControl } from '../../../components/analytics/RangeControl.jsx';
import { rangeToParams } from '../../../components/analytics/rangeParams.js';
import { DeltaKpi } from '../../../components/analytics/DeltaKpi.jsx';
import { SortableTable } from '../../../components/analytics/SortableTable.jsx';
import { CostTrendChart } from '../../../components/charts/CostTrendChart.jsx';
import { downloadCsv } from '../../../lib/csv.js';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

const KIND_LABELS = { chat: 'Chat', voice: 'Voce', embedding: 'Embedding (RAG)', transcription: 'Trascrizione', playground: 'Playground' };
const METRIC_TABS = [{ value: 'costUsd', label: 'Costo' }, { value: 'calls', label: 'Chiamate' }, { value: 'totalTokens', label: 'Token' }];

const SECTION_LABELS = {
  kpi: 'Indicatori principali',
  trend: 'Andamento',
  byKind: 'Spesa per tipo',
  byModel: 'Spesa per modello',
  daily: 'Dettaglio giornaliero',
  products: 'Prodotti più richiesti',
  agents: 'Agenti più usati',
  employee: 'Dettaglio dipendente',
  audit: 'Attività amministrativa'
};
const DEFAULT_ORDER = ['kpi', 'trend', 'byKind', 'byModel', 'daily', 'products', 'agents', 'employee', 'audit'];
const STORAGE_KEY = 'orme.analytics.layout.v1';

function loadLayout() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (saved && Array.isArray(saved.order)) {
      const known = saved.order.filter((id) => DEFAULT_ORDER.includes(id));
      const missing = DEFAULT_ORDER.filter((id) => !known.includes(id));
      return { order: [...known, ...missing], hidden: Array.isArray(saved.hidden) ? saved.hidden : [] };
    }
  } catch { /* ignore malformed layout */ }
  return { order: DEFAULT_ORDER, hidden: [] };
}

function delta(cur, prev) {
  return prev > 0 ? (cur - prev) / prev : null;
}

function durationLabel(ms) {
  if (!ms) return '0 s';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

function ExportButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-ink-muted transition-colors hover:border-border-strong hover:text-ink">
      <Download className="h-3.5 w-3.5" /> CSV
    </button>
  );
}

function SectionCard({ title, hint, action, children }) {
  return (
    <Card>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          {hint && <p className="text-xs text-ink-muted">{hint}</p>}
        </div>
        {action}
      </header>
      {children}
    </Card>
  );
}

// ----- employee drill-down -------------------------------------------------

function EmployeeSection({ params }) {
  const usersQuery = useQuery({ queryKey: ['users', 'all', 'analytics'], queryFn: () => usersApi.list({ limit: 100 }) });
  const users = usersQuery.data?.items || [];
  const [userId, setUserId] = useState('');
  const reportQuery = useQuery({
    queryKey: ['analytics', 'employee', userId, params],
    enabled: Boolean(userId),
    queryFn: () => analyticsApi.employeeReport(userId, params)
  });
  const data = reportQuery.data;

  return (
    <SectionCard title={SECTION_LABELS.employee} hint="Attività di un singolo dipendente nel periodo.">
      <Select label="Dipendente" className="mb-4 w-72" value={userId} onChange={(e) => setUserId(e.target.value)}>
        <option value="">Seleziona...</option>
        {users.map((u) => <option key={u._id} value={u._id}>{u.email}</option>)}
      </Select>
      {!userId ? (
        <p className="text-sm text-ink-muted">Seleziona un dipendente per il report dedicato.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard label="Conversazioni" loading={reportQuery.isLoading} value={data ? fmt.int(data.conversations) : '-'} />
          <KpiCard label="Chiamate AI" loading={reportQuery.isLoading} value={data ? fmt.int(data.aiCalls) : '-'} sub={data ? `${fmt.int(data.totalTokens)} token` : null} />
          <KpiCard label="Costo AI" loading={reportQuery.isLoading} value={data ? fmt.usd(data.costUsd) : '-'} sub={data ? `Latenza media ${fmt.durationMs(data.avgLatencyMs)}` : null} />
          <KpiCard label="Tasso di errore" loading={reportQuery.isLoading} value={data ? fmt.pct(data.errorRate) : '-'} sub={data?.lastInteractionAt ? `Ultima attività ${fmt.dateTime(data.lastInteractionAt)}` : 'Mai attivo'} />
        </div>
      )}
    </SectionCard>
  );
}

// ----- store agents (requires a selected store) ----------------------------

function AgentsSection({ storeId, params }) {
  const reportQuery = useQuery({
    queryKey: ['analytics', 'store', storeId, params],
    enabled: Boolean(storeId),
    queryFn: () => analyticsApi.storeReport(storeId, params)
  });
  const topAgents = reportQuery.data?.topAgents || [];

  return (
    <SectionCard title={SECTION_LABELS.agents} hint="Disponibile selezionando un negozio nel filtro in alto.">
      {!storeId ? (
        <p className="text-sm text-ink-muted">Seleziona un negozio per vedere gli agenti più usati.</p>
      ) : reportQuery.isLoading ? <Skeleton className="h-24 w-full" /> : topAgents.length === 0 ? (
        <p className="text-sm text-ink-muted">Nessuna interazione con agenti.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {topAgents.map((a) => (
            <li key={a.agentId} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
              <Badge>{a.agentId}</Badge>
              <span className="text-xs text-ink-muted">{fmt.int(a.conversations)} conv. · {fmt.int(a.messages)} msg</span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

// ----- main page -----------------------------------------------------------

export function AnalyticsPage() {
  const { hasPermission } = useAuth();
  const [range, setRange] = useState({ period: '30d' });
  const [storeId, setStoreId] = useState('');
  const [compare, setCompare] = useState(true);
  const [metric, setMetric] = useState('costUsd');
  const [layout, setLayout] = useState(loadLayout);
  const [customizing, setCustomizing] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ order: layout.order, hidden: layout.hidden }));
  }, [layout]);

  const storesQuery = useQuery({ queryKey: ['stores', 'all'], queryFn: () => storesApi.list({ limit: 100 }) });
  const stores = storesQuery.data?.items || [];

  const params = useMemo(() => ({ ...rangeToParams(range), ...(storeId ? { storeId } : {}) }), [range, storeId]);

  const overviewQuery = useQuery({ queryKey: ['analytics', 'overview', params], queryFn: () => analyticsApi.overview(params), enabled: hasPermission('analytics:read') });
  const costQuery = useQuery({ queryKey: ['analytics', 'ai-cost', params], queryFn: () => analyticsApi.aiCost(params), enabled: hasPermission('analytics:read') });

  // Previous equivalent window, derived from the resolved current period.
  const prevParams = useMemo(() => {
    const p = overviewQuery.data?.period;
    if (!compare || !p) return null;
    const from = new Date(p.from).getTime();
    const to = new Date(p.to).getTime();
    const len = to - from;
    if (!len) return null;
    return { from: new Date(from - len).toISOString(), to: new Date(from).toISOString(), ...(storeId ? { storeId } : {}) };
  }, [compare, overviewQuery.data, storeId]);

  const prevOverviewQuery = useQuery({ queryKey: ['analytics', 'overview', 'prev', prevParams], enabled: Boolean(prevParams), queryFn: () => analyticsApi.overview(prevParams) });

  if (!hasPermission('analytics:read')) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('nav.analytics')} />
        <Card><p className="text-sm text-ink-muted">Permesso <code>analytics:read</code> richiesto.</p></Card>
      </div>
    );
  }

  const ai = overviewQuery.data?.ai;
  const prev = prevOverviewQuery.data?.ai;
  const loading = overviewQuery.isLoading || costQuery.isLoading;
  const top = overviewQuery.data?.topProducts || [];
  const audit = overviewQuery.data?.auditActivity || [];
  const daily = costQuery.data?.daily || [];
  const byKind = (costQuery.data?.byKind || []).map((k) => ({ ...k, kindLabel: KIND_LABELS[k.kind] || k.kind }));
  const byModel = costQuery.data?.byModel || [];

  const hidden = new Set(layout.hidden);
  const toggleHidden = (id) => setLayout((l) => ({ ...l, hidden: l.hidden.includes(id) ? l.hidden.filter((x) => x !== id) : [...l.hidden, id] }));
  const move = (id, dir) => setLayout((l) => {
    const order = [...l.order];
    const i = order.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= order.length) return l;
    [order[i], order[j]] = [order[j], order[i]];
    return { ...l, order };
  });
  const resetLayout = () => setLayout({ order: DEFAULT_ORDER, hidden: [] });

  const SECTIONS = {
    kpi: (
      <section key="kpi" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <DeltaKpi label="Costo AI" loading={loading} value={ai ? fmt.usd(ai.costUsd) : '-'} sub={ai ? `${fmt.int(ai.aiCalls)} chiamate` : null} delta={ai && prev ? delta(ai.costUsd, prev.costUsd) : null} goodWhen="down" />
        <DeltaKpi label="Conversazioni" loading={loading} value={ai ? fmt.int(ai.conversations) : '-'} sub={ai ? `${fmt.int(ai.messages)} messaggi` : null} delta={ai && prev ? delta(ai.conversations, prev.conversations) : null} goodWhen="up" />
        <DeltaKpi label="Token totali" loading={loading} value={ai ? fmt.int(ai.totalTokens) : '-'} sub={ai ? `Latenza media ${fmt.durationMs(ai.avgLatencyMs)}` : null} delta={ai && prev ? delta(ai.totalTokens, prev.totalTokens) : null} goodWhen="up" />
        <DeltaKpi label="Tasso di errore" loading={loading} value={ai ? fmt.pct(ai.errorRate) : '-'} sub={ai ? `Durata media sessione ${durationLabel(ai.avgSessionDurationMs)}` : null} delta={ai && prev ? delta(ai.errorRate, prev.errorRate) : null} goodWhen="down" />
      </section>
    ),
    trend: (
      <SectionCard key="trend" title={SECTION_LABELS.trend} hint="Valore giornaliero nel periodo selezionato." action={<Tabs value={metric} onChange={setMetric} items={METRIC_TABS} />}>
        {loading ? <Skeleton className="h-64 w-full" /> : <CostTrendChart data={daily} metric={metric} />}
      </SectionCard>
    ),
    byKind: (
      <SectionCard key="byKind" title={SECTION_LABELS.byKind} hint="Spesa e chiamate per tipo di utilizzo." action={<ExportButton onClick={() => downloadCsv('analytics-per-tipo', byKind, [{ header: 'Tipo', value: (r) => r.kindLabel }, { header: 'Chiamate', value: (r) => r.calls }, { header: 'Costo USD', value: (r) => r.costUsd }])} />}>
        {loading ? <Skeleton className="h-40 w-full" /> : (
          <SortableTable
            rows={byKind}
            initialSort={{ key: 'costUsd', dir: 'desc' }}
            emptyText="Nessun utilizzo nel periodo."
            columns={[
              { key: 'kindLabel', header: 'Tipo' },
              { key: 'calls', header: 'Chiamate', align: 'right', render: (r) => fmt.int(r.calls) },
              { key: 'costUsd', header: 'Costo', align: 'right', render: (r) => fmt.usd(r.costUsd) }
            ]}
          />
        )}
      </SectionCard>
    ),
    byModel: (
      <SectionCard key="byModel" title={SECTION_LABELS.byModel} hint="Quali modelli incidono di più." action={<ExportButton onClick={() => downloadCsv('analytics-per-modello', byModel, [{ header: 'Modello', value: (r) => r.model }, { header: 'Chiamate', value: (r) => r.calls }, { header: 'Token', value: (r) => r.totalTokens }, { header: 'Costo USD', value: (r) => r.costUsd }])} />}>
        {loading ? <Skeleton className="h-40 w-full" /> : (
          <SortableTable
            rows={byModel}
            initialSort={{ key: 'costUsd', dir: 'desc' }}
            emptyText="Nessun modello utilizzato."
            columns={[
              { key: 'model', header: 'Modello', render: (r) => <code className="text-xs">{r.model}</code> },
              { key: 'calls', header: 'Chiamate', align: 'right', render: (r) => fmt.int(r.calls) },
              { key: 'totalTokens', header: 'Token', align: 'right', render: (r) => fmt.int(r.totalTokens) },
              { key: 'costUsd', header: 'Costo', align: 'right', render: (r) => fmt.usd(r.costUsd) }
            ]}
          />
        )}
      </SectionCard>
    ),
    daily: (
      <SectionCard key="daily" title={SECTION_LABELS.daily} hint="Numeri giorno per giorno." action={<ExportButton onClick={() => downloadCsv('analytics-giornaliero', daily, [{ header: 'Data', value: (r) => r.date }, { header: 'Chiamate', value: (r) => r.calls }, { header: 'Token', value: (r) => r.totalTokens }, { header: 'Costo USD', value: (r) => r.costUsd }])} />}>
        {loading ? <Skeleton className="h-40 w-full" /> : (
          <SortableTable
            rows={daily}
            initialSort={{ key: 'date', dir: 'desc' }}
            emptyText="Nessun dato nel periodo."
            columns={[
              { key: 'date', header: 'Data', render: (r) => fmt.date(r.date) },
              { key: 'calls', header: 'Chiamate', align: 'right', render: (r) => fmt.int(r.calls) },
              { key: 'totalTokens', header: 'Token', align: 'right', render: (r) => fmt.int(r.totalTokens) },
              { key: 'costUsd', header: 'Costo', align: 'right', render: (r) => fmt.usd(r.costUsd) }
            ]}
          />
        )}
      </SectionCard>
    ),
    products: (
      <SectionCard key="products" title={SECTION_LABELS.products} hint="Dalle risposte dell'assistente AI." action={top.length ? <ExportButton onClick={() => downloadCsv('analytics-prodotti', top, [{ header: 'Prodotto', value: (r) => r.name || r.sku || r.id }, { header: 'Marca', value: (r) => r.brand || '' }, { header: 'Richieste', value: (r) => r.hits }])} /> : null}>
        {loading ? <Skeleton className="h-40 w-full" /> : top.length === 0 ? (
          <p className="text-sm text-ink-muted">Nessun prodotto rilevato.</p>
        ) : (
          <SortableTable
            rows={top}
            initialSort={{ key: 'hits', dir: 'desc' }}
            columns={[
              { key: 'name', header: 'Prodotto', sortValue: (r) => r.name || r.sku || r.id, render: (r) => <span className="text-ink">{r.name || r.sku || r.id}</span> },
              { key: 'brand', header: 'Marca', render: (r) => <span className="text-ink-muted">{r.brand || '-'}</span> },
              { key: 'hits', header: 'Richieste', align: 'right', render: (r) => fmt.int(r.hits) }
            ]}
          />
        )}
      </SectionCard>
    ),
    agents: <AgentsSection key="agents" storeId={storeId} params={params} />,
    employee: <EmployeeSection key="employee" params={params} />,
    audit: (
      <SectionCard key="audit" title={SECTION_LABELS.audit} hint="Azioni sul pannello aggregate per tipo e gravità.">
        {loading ? <Skeleton className="h-32 w-full" /> : audit.length === 0 ? (
          <p className="text-sm text-ink-muted">Nessuna attività nel periodo.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {audit.slice(0, 18).map((row, idx) => (
              <li key={idx} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                <span className="truncate text-ink">{row.action}</span>
                <span className="flex items-center gap-2">
                  <Badge tone={row.severity === 'critical' ? 'danger' : row.severity === 'warning' ? 'warning' : row.severity === 'notice' ? 'info' : 'neutral'}>{row.severity}</Badge>
                  <span className="text-xs text-ink-muted">{fmt.int(row.count)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    )
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('nav.analytics')} description="Analisi dettagliata e personalizzabile dell'attività AI." />

      {/* Filtri */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-elevated p-4">
        <RangeControl value={range} onChange={setRange} />
        <Select className="w-56" value={storeId} onChange={(e) => setStoreId(e.target.value)}>
          <option value="">Tutti i negozi</option>
          {stores.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.code})</option>)}
        </Select>
        <label className="ml-auto flex items-center gap-2 text-sm text-ink-muted">
          Confronto periodo prec.
          <Switch checked={compare} onChange={setCompare} />
        </label>
        <Button variant="secondary" size="sm" onClick={() => setCustomizing((v) => !v)}>
          <SlidersHorizontal className="h-4 w-4" /> Personalizza
        </Button>
      </div>

      {/* Pannello personalizzazione */}
      {customizing && (
        <Card>
          <header className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-ink">Personalizza la vista</h2>
              <p className="text-xs text-ink-muted">Mostra/nascondi e riordina le sezioni. Le preferenze restano salvate su questo browser.</p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={resetLayout} className="text-xs text-ink-muted hover:text-ink">Ripristina</button>
              <button type="button" onClick={() => setCustomizing(false)} className="rounded-md p-1 text-ink-muted hover:text-ink"><X className="h-4 w-4" /></button>
            </div>
          </header>
          <ul className="flex flex-col divide-y divide-border">
            {layout.order.map((id, idx) => (
              <li key={id} className="flex items-center justify-between gap-3 py-2">
                <span className="text-sm text-ink">{SECTION_LABELS[id]}</span>
                <span className="flex items-center gap-1">
                  <button type="button" disabled={idx === 0} onClick={() => move(id, -1)} className="rounded p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" disabled={idx === layout.order.length - 1} onClick={() => move(id, 1)} className="rounded p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <Switch checked={!hidden.has(id)} onChange={() => toggleHidden(id)} />
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Sezioni componibili */}
      {layout.order.filter((id) => !hidden.has(id)).map((id) => SECTIONS[id])}
    </div>
  );
}
